<soft-page>
  @if (workNote(); as workNote) {
    <soft-page-header>
      <div class="flex flex-row items-center justify-start gap-1">
        <div class="flex flex-col justify-center items-start flex-nowrap">
          @if (workNote?.status !== 'O') {
            <div
              class="uppercase inline-block grow-0 w-fit mb-2 bg-warning-100 px-1.5 py-0.5 font-semibold rounded-full text-xs"
              style="color: var(--colorWarning600)"
            >
              Bereits erledigt!
            </div>
          }
          @if (workNote?.kzmuster === 'J') {
            <span class="uppercase mt-1 mb-1" style="color: var(--colorWarning)">
              Muster!
            </span>
          }
          <span class="soft-overline color-overline mb-1">
            Auftrag {{ workNote?.kopfau?.belegnummer
            }}{{
              workNote?.kopfau?.kundeadresse ? ' - ' + workNote?.kopfau?.kundeadresse : ''
            }}
            @if (hasImages$ | async) {
              <i class="fa-regular fa-images ml-1"></i>
            }
          </span>
          <span class="text-xl mb-1">
            <strong>
              {{ workNote?.bezeichnung }}
              @if (workNote?.belegdatum) {
                - {{ $safeNavigationMigration(workNote?.belegdatum) | format: 'DATE.DEFAULT' }}
              }
            </strong>
          </span>
          @if (workNote?.kopfau?.baustelle) {
            <span class="color-light">{{ workNote?.kopfau?.baustelle }}</span>
          }
        </div>
      </div>
    </soft-page-header>
  }

  <soft-page-panel panelStyle="plain">
    @if (workNote$ | async; as workNote) {
      <ng-container>
        <div
          class="flex flex-col justify-center flex-nowrap mt-3 mb-3 soft-panel-grouped min-h-auto! rounded border-box animate-height-changes"
        >
          <div
            class="flex flex-row justify-between flex-nowrap center-v p-4 border-box"
            (click)="isExpanded = !isExpanded"
          >
            <div class="text-lg">
              <strong>{{ '#WORK_NOTE.DETAIL.KOPFDATEN' | translate }}</strong>
            </div>
            <i
              class="fa-regular"
              [ngClass]="isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </div>
          @if (isExpanded) {
            <div class="flex flex-col" style="overflow: hidden" @shrinkOrGrow>
              @if (workNote?.kopfau?.baustelle) {
                <article
                  class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
                >
                  <span class="soft-overline color-overline mb-1">
                    {{ '#WORK_NOTE.DETAIL.BAUSTELLE' | translate }}
                  </span>
                  <span class="soft-text">{{ workNote?.kopfau?.baustelle }}</span>
                </article>
              }
              @if (workNote.kopfau.objekt.number || workNote.kopfau.objekt.name) {
                <article
                  class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
                >
                  <span class="soft-overline color-overline mb-1">
                    {{ '#WORK_NOTE.DETAIL.OBJEKT' | translate }}
                  </span>
                  <span class="soft-text">
                    Objekt {{ workNote.kopfau.objekt.number }}
                    @if (workNote.kopfau.objekt.name) {
                      - {{ workNote.kopfau.objekt.name }}
                    }
                  </span>
                </article>
              }
              @if (workNote?.kopftext) {
                <article
                  class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
                >
                  <span class="soft-overline color-overline mb-1">
                    {{ '#WORK_NOTE.DETAIL.KOPFTEXT' | translate }}
                  </span>
                  <span class="soft-text">{{ workNote?.kopftext }}</span>
                </article>
              }

              @if (workNoteConfig?.appointmentTypeSelection) {
                <section class="flex flex-col p-4 w-full border-t">
                  <h6 class="soft-overline">Terminart</h6>

                  <div class="flex gap-2">
                    <soft-select
                      [value]="$safeNavigationMigration(workNote?.kzterminart)"
                      (valueChange)="updateAppointmentType(workNote, $event)"
                    >
                      @for (
                        appointmentTypeKey of appointmentTypeKeys ?? [];
                        track appointmentTypeKey
                      ) {
                        @let appointmentType =
                          getDefinitionForAppointmentType(appointmentTypeKey);
                        <soft-select-option [value]="appointmentTypeKey">
                          <div class="flex flex-row gap-2 items-center">
                            <soft-icon [name]="appointmentType.icon" />
                            <div>
                              {{ appointmentType.label }}
                            </div>
                          </div>
                        </soft-select-option>
                      }
                    </soft-select>
                  </div>
                </section>
              }

              @if ({ readonly: readonly$ | async }; as vm) {
                @if (!workNoteConfig || !workNoteConfig?.hideWeatherSection) {
                  <article
                    class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
                  >
                    <span class="soft-overline color-overline mb-1">
                      {{ '#WORK_NOTE.DETAIL.WEATHER' | translate }}
                    </span>
                    <form
                      class="g gtc-2"
                      [ngClass]="workNoteConfig?.showVermerkInput ? 'gtr-3' : 'gtr-2'"
                      style="grid-gap: 1rem"
                      [formGroup]="form"
                    >
                      <div class="gc-1 gr-1">
                        <div class="flex flex-col justify-center flex-nowrap">
                          <span class="text-xs text-gray-600 mb-1">
                            {{ '#WORK_NOTE.DETAIL.TEMP_MIN' | translate }}
                          </span>
                          <soft-number-input
                            [readonly]="vm.readonly ?? false"
                            changeTrigger="input"
                            formControlName="temperatur_min"
                          />
                          @if (form.controls['temperatur_min'].touched && !vm.readonly) {
                            <soft-validation-container
                              [validation]="form | validationMessage: 'temperatur_min'"
                              class="soft-property-validation"
                            />
                          }
                        </div>
                      </div>
                      <div class="gc-2 gr-1">
                        <div class="flex flex-col justify-center flex-nowrap">
                          <span class="text-xs text-gray-600 mb-1">
                            {{ '#WORK_NOTE.DETAIL.TEMP_MAX' | translate }}
                          </span>
                          <soft-number-input
                            [readonly]="vm.readonly ?? false"
                            changeTrigger="input"
                            formControlName="temperatur_max"
                          />
                          @if (form.controls['temperatur_max'].touched && !vm.readonly) {
                            <soft-validation-container
                              [validation]="form | validationMessage: 'temperatur_max'"
                              class="soft-property-validation"
                            />
                          }
                        </div>
                      </div>
                      <div class="gc-1-2 gr-2">
                        <div class="flex flex-col justify-center flex-nowrap">
                          <span class="text-xs text-gray-600 mb-1">
                            {{ '#WORK_NOTE.DETAIL.WEATHER' | translate }}
                          </span>
                          <soft-select
                            formControlName="kzWetter"
                            [readonly]="vm.readonly ?? false"
                          >
                            @for (option of kzWetterOptions$ | async; track option) {
                              <soft-select-option [value]="$safeNavigationMigration(option?.value)">
                                {{ option?.description }}
                              </soft-select-option>
                            }
                          </soft-select>
                        </div>
                      </div>
                    </form>
                  </article>
                }
              }
              @if (
                workNote?.kopfau?.objekt?.geodaten &&
                (workNote?.kopfau?.objekt?.geodaten?.gpsbreite ||
                  workNote?.kopfau?.objekt?.geodaten?.gpslaenge)
              ) {
                <article
                  class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
                >
                  <div class="flex flex-row justify-between items-center">
                    <span class="soft-overline color-overline mb-1">
                      {{ '#WORK_NOTE.DETAIL.GEO.TITLE' | translate }}
                    </span>
                    <a
                      target="_blank"
                      class="soft-link"
                      [attr.href]="getGoogleLink($safeNavigationMigration(workNote?.kopfau?.objekt))"
                    >
                      {{ '#WORK_NOTE.DETAIL.GEO.GOOGLE_MAPS' | translate }}
                    </a>
                  </div>
                  <div class="soft-card soft-card-rounded w-full border-bottom mt-1">
                    <div class="flex flex-row justify-between pt-1 pb-2 border-bottom">
                      <div class="ml-1">
                        <strong>
                          {{ '#WORK_NOTE.DETAIL.GEO.GPSBREITE' | translate }}
                        </strong>
                      </div>
                      <div class="mr-1">
                        {{ workNote?.kopfau?.objekt?.geodaten?.gpsbreite || '-' }}
                      </div>
                    </div>
                    <div class="flex flex-row justify-between pt-2 pb-1">
                      <div class="ml-1">
                        <strong>
                          {{ '#WORK_NOTE.DETAIL.GEO.GPSLAENGE' | translate }}
                        </strong>
                      </div>
                      <div class="mr-1">
                        {{ workNote?.kopfau?.objekt?.geodaten?.gpslaenge || '-' }}
                      </div>
                    </div>
                  </div>
                </article>
              }
              <article
                class="flex flex-col justify-center flex-nowrap border-top p-4 border-box"
              >
                <span class="soft-overline color-overline mb-1">
                  {{ '#WORK_NOTE.DETAIL.IMAGES' | translate }}
                </span>
                <soft-archive-list
                  [archiveKey]="convertArchiveKey(workNote?.archiveKey || '')"
                  style="width: 100%"
                />
              </article>
            </div>
          }
        </div>

        @if (workNoteConfig?.employeeSelection) {
          <section class="flex flex-col mt-5">
            <h6 class="soft-overline">Teilnehmer</h6>

            <div class="flex gap-2 justify-start items-start flex-wrap">
              <soft-chip-group class="contents!">
                @for (employee of workNote?.mitarbeiter ?? []; track employee.id) {
                  <soft-chip
                    [canDelete]="true"
                    (delete)="removeEmployee(workNote, employee)"
                  >
                    {{ employee.persnr }} - {{ employee.vorname }} {{ employee.zuname }}
                  </soft-chip>
                }
              </soft-chip-group>
              <button
                type="button"
                (click)="addEmployeeModal(workNote)"
                class="rounded-full h-auto shrink-0 grow-0 cursor-pointer w-auto border-2 px-2 py-0.5 text-sm font-semibold hover:bg-primary-500 hover:text-primary-contrast border-dashed text-center border-primary text-primary"
              >
                Teilnehmer hinzufügen
              </button>
            </div>
          </section>
        }

        @if (bewegungen$ | async; as bewegungen) {
          @if (bewegungen.length > 0) {
            <section class="flex flex-col mt-5">
              <div class="flex flex-row justify-between flex-nowrap center-v mb-2">
                <span class="soft-overline color-overline">
                  {{ '#WORK_NOTE.DETAIL.POSITIONS' | translate }}
                </span>
                @if (workNote?.kzmuster !== 'J') {
                  <i
                    class="fa-regular fa-circle-check color-primary"
                    (click)="fillAllSuggestedValues(workNote.id, bewegungen)"
                    style="font-size: 1.25rem"
                  ></i>
                }
              </div>
              <div class="flex flex-col" @stagger>
                @for (
                  bewegung of bewegungen;
                  track $any(trackByFn)($index, bewegung);
                  let first = $first
                ) {
                  <lib-work-note-item-card
                    @fade
                    [noteEditable]="workNote?.kzmuster !== 'J'"
                    [class.mt-3]="!first"
                    [readonly]="(readonly$ | async) ?? false"
                    (copyTapped)="copyItem(bewegung, workNote)"
                    (editNoteTapped)="openNoteDialog(bewegung, workNote)"
                    (doneQuantityChange)="setDoneAmount(workNote.id, bewegung, $event)"
                    [bewegung]="bewegung"
                  />
                }
              </div>
            </section>
          }
        }
        @if (
          (!(bewegungen$ | async) || ((bewegungen$ | async)?.length ?? 0) < 1) &&
          (loading$ | async)
        ) {
          <div class="min-h-40 flex flex-row justify-center">
            <div class="flex flex-row justify-center p-5 mt-5" style="height: 100%">
              <soft-loading-spinner class="spin" />
            </div>
          </div>
        }

        @if (workNoteConfig?.showVermerkInternInput) {
          <article class="c column center-v no-wrap border-box mt-5">
            <div class="flex items-end justify-between w-full">
              <span class="soft-overline color-overline mb-1">
                {{ '#WORK_NOTE.DETAIL.VERMERK_INTERN' | translate }}
              </span>
              <button
                type="button"
                class="soft-button primary px-2! py-1! rounded"
                [disabled]="vermerkInternInput?.value === workNote?.vermerk_intern"
                (click)="updateVermerkIntern(vermerkInternInput?.value ?? null, workNote)"
              >
                Speichern
              </button>
            </div>
            <soft-multiline-input
              class="mt-1"
              #vermerkInternInput
              [value]="workNote?.vermerk_intern ?? null"
              (valueChange)="vermerkInternLocal.set($event ?? null)"
              [lines]="7"
              placeholder="Vermerk Intern"
            />
          </article>
        }

        @if (workNoteConfig?.showVermerkInput) {
          <article class="flex flex-col justify-center flex-nowrap border-box mt-5">
            <div class="flex items-end justify-between w-full">
              <span class="soft-overline color-overline mb-1">
                {{ '#WORK_NOTE.DETAIL.VERMERK' | translate }}
              </span>
              <button
                type="button"
                class="soft-button primary px-2! py-1! rounded"
                [disabled]="vermerkInput?.value === workNote?.vermerk"
                (click)="updateVermerk(vermerkInput?.value ?? null, workNote)"
              >
                Speichern
              </button>
            </div>
            <soft-multiline-input
              class="mt-1"
              #vermerkInput
              [value]="workNote?.vermerk ?? null"
              (valueChange)="vermerkLocal.set($event ?? null)"
              [lines]="7"
              placeholder="Vermerk"
            />
          </article>
        }
        @if (workNoteConfig?.showDrawPad) {
          <section class="flex flex-col mt-5 mb-5">
            <div class="flex flex-row justify-between flex-nowrap center-v mb-0 mt-1">
              <span class="soft-overline color-overline">
                {{ '#WORK_NOTE.DETAIL.DRAW_PAD' | translate }}
              </span>
            </div>
            <soft-draw-input
              [readonly]="(readonly$ | async) ?? false"
              [value]="$safeNavigationMigration(workNote?.zeichnung) | stringToBlob"
              (valueChange)="drawInputChanged(workNote, $event)"
            />
          </section>
        }
        <section class="flex flex-col mt-5 mb-5 bottom-padding">
          <div class="flex flex-row justify-between flex-nowrap center-v mb-0 mt-1">
            <span class="soft-overline color-overline soft-label required">
              {{ '#WORK_NOTE.DETAIL.SIGNATURE' | translate }}
            </span>
          </div>
          <soft-signature-input
            [readonly]="(readonly$ | async) ?? false"
            (valueChange)="saveSignature(workNote, $event)"
            [value]="$safeNavigationMigration(workNote?.signature) | loadSignature | async"
            #signature
          />
        </section>
      </ng-container>
    }
  </soft-page-panel>
</soft-page>

@if ((isSaving$ | async) || downloading()) {
  <soft-blocking-loading-spinner />
}

<ng-template #submitButtonTemplate>
  @if (isReadonly()) {
    <button
      class="soft-button accent h-14 w-full!"
      (click)="navigateBack()"
      type="button"
    >
      Schließen
    </button>
  } @else if (workNote(); as workNote) {
    @if (!!workNote?.signature) {
      <button
        class="soft-button accent h-14 w-full!"
        (click)="save(workNote)"
        type="button"
      >
        Speichern
      </button>
    } @else {
      <button
        class="soft-button accent h-14 w-full!"
        (click)="markAsDone(workNote)"
        type="button"
      >
        Speichern und Abschließen
      </button>
    }
  }
</ng-template>

<ng-template #menuBarLeadingTemplate>
  @if (workNote()?.status === 'O' && workNote()?.kzmuster !== 'J') {
    <soft-menu-bar-button
      variant="plain"
      icon="fa-regular fa-upload"
      label="Datei(en) hochladen"
      responsive
      (execute)="uploadFile()"
    />
    <soft-menu-bar-button
      variant="plain"
      icon="fa-regular fa-eye"
      label="Druckvorschau"
      responsive
      (execute)="downloadPDFPreview()"
    />
  }
</ng-template>

<ng-template #menuBarTrailingTemplate>
  @let note = workNote();

  <div class="flex items-center gap-1">
    @if (
      (!note?.status || note?.status !== 'O' || note?.kzmuster === 'J') &&
      workNoteConfig?.showCopyAction
    ) {
      <soft-menu-bar-button
        variant="plain"
        icon="fa-regular fa-copy"
        label="Kopieren"
        (execute)="copyWorkNote()"
      />
    } @else if (!!note?.status && note?.status === 'O') {
      @if (note?.kzmuster === 'J' && workNoteConfig?.showCopyAction) {
        <soft-menu-bar-button
          variant="plain"
          icon="fa-regular fa-copy"
          label="Kopieren"
          (execute)="copyWorkNote()"
          7
        />
      }
      @if (canSubmit()) {
        <soft-menu-bar-button
          variant="outline"
          icon="fa-regular fa-check"
          label="Abschluss"
          (execute)="note && markAsDone(note)"
        />
      }
      <soft-menu-bar-button
        variant="primary"
        icon="fa-regular fa-save"
        label="Speichern"
        (execute)="note && save(note)"
      />
    }
  </div>
</ng-template>

<ng-template #exampleTemplate1>
  <div class="flex items-center gap-2">
    <button class="text-center flex items-center justify-center p-1" type="button">
      <soft-icon name="fa-regular fa-search" />
    </button>

    <button class="text-center flex items-center justify-center p-1" type="button">
      <soft-icon name="fa-regular fa-grid" />
    </button>
  </div>
</ng-template>

<ng-template #exampleTemplate2>
  <button class="text-center flex items-center" type="button">
    <soft-icon name="fa-regular fa-search" />
  </button>
</ng-template>

<ng-template #exampleTemplate3>
  <button class="text-center flex items-center" type="button">
    <soft-icon name="fa-regular fa-grid" />
  </button>
</ng-template>
